'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FooterLink {
  label: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

const Footer = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState('2026');

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear().toString());
  }, []);

  const footerSections: FooterSection[] = [
    {
      title: "Services",
      links: [
        { label: "Minerals Transport", href: "/services-overview" },
        { label: "Heavy Equipment", href: "/services-overview" },
        { label: "Cross-Border Solutions", href: "/regional-coverage" },
        { label: "Customs Brokerage", href: "/services-overview" }
      ]
    },
    {
      title: "Company",
      links: [
        { label: "About Us", href: "/about" },
        { label: "Regional Coverage", href: "/regional-coverage" },
        { label: "Safety & Compliance", href: "/safety-and-compliance" },
        { label: "Contact", href: "/contact-and-quotes" }
      ]
    },
    {
      title: "Resources",
      links: [
        { label: "Get Quote", href: "/contact-and-quotes" },
        { label: "Track Shipment", href: "/contact-and-quotes" },
        { label: "Route Guides", href: "/regional-coverage" },
        { label: "Documentation", href: "/safety-and-compliance" }
      ]
    }
  ];

  const socialLinks = [
    { name: "LinkedIn", icon: "LinkIcon", href: "#" },
    { name: "Twitter", icon: "AtSymbolIcon", href: "#" },
    { name: "Facebook", icon: "ShareIcon", href: "#" }
  ];

  const contactInfo = [
    { icon: "PhoneIcon", text: "+243981944976", href: "tel:+243981944976" },
    { icon: "EnvelopeIcon", text: "mylesdrivelc@gmail.com", href: "mailto:mylesdrivelc@gmail.com" },
    { icon: "MapPinIcon", text: "Kampala, Uganda", href: "#" }
  ];

  return (
    <footer className="bg-primary text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link href="/homepage" className="flex items-center space-x-2 mb-4">
              <div className="flex flex-col">
                <span className="font-headline font-bold text-xl text-white leading-tight">
                  MylesDrive
                </span>
                <span className="font-body text-xs text-white/70 leading-tight">
                  Logistics Hub
                </span>
              </div>
            </Link>
            <p className="font-body text-sm text-gray-300 mb-4 leading-relaxed">
              Premium B2B logistics platform specializing in African extractives and heavy industry transport across East, Central, and Southern Africa.
            </p>
            <div className="space-y-2">
              {contactInfo.map((contact, index) => (
                <a
                  key={index}
                  href={contact.href}
                  className="flex items-center space-x-2 text-sm text-gray-300 hover:text-golden-light transition-colors duration-300"
                >
                  <Icon name={contact.icon as any} size={16} />
                  <span>{contact.text}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Footer Links */}
          {footerSections.map((section, index) => (
            <div key={index}>
              <h3 className="font-headline font-bold text-base text-white mb-4">
                {section.title}
              </h3>
              <ul className="space-y-2">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      href={link.href}
                      className="font-body text-sm text-gray-300 hover:text-golden-light transition-colors duration-300"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="font-body text-sm text-gray-300">
              {isHydrated ? `© ${currentYear}` : '© 2026'} MylesDrive Logistics Hub. All rights reserved.
            </p>

            <div className="flex items-center space-x-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full hover:bg-gradient-to-br hover:from-golden-light hover:to-golden-dark transition-all duration-300"
                  aria-label={social.name}
                >
                  <Icon name={social.icon as any} size={20} />
                </a>
              ))}
            </div>

            <div className="flex items-center space-x-4">
              <Link
                href="#"
                className="font-body text-sm text-gray-300 hover:text-golden-light transition-colors duration-300"
              >
                Privacy Policy
              </Link>
              <span className="text-gray-600">|</span>
              <Link
                href="#"
                className="font-body text-sm text-gray-300 hover:text-golden-light transition-colors duration-300"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;